import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Divider, 
  Button, 
  FormControl,
  Select, 
  MenuItem, 
  Tab, 
  Tabs, 
  CircularProgress,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  alpha,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  DateRange as DateRangeIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Analytics as AnalyticsIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { useTheme } from '@mui/material/styles';
import { API_URL } from '../config';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const StatCard = ({ title, value, trend, trendLabel, icon, color, suffix }) => {
  const theme = useTheme();
  
  // Helper function to determine trend color
  const getTrendColor = (trendValue) => {
    if (trendValue === 0) return theme.palette.text.secondary;
    return trendValue > 0 ? theme.palette.success.main : theme.palette.error.main;
  };

  return (
    <Card sx={{ p: 2.5, height: '100%', width: '100%' }}>
      <Typography 
        color="text.secondary" 
        variant="body2"
        sx={{ 
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          opacity: 0.7
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="h4" 
        sx={{ 
          my: 1,
          fontSize: '1.75rem',
          fontWeight: 700
        }}
      >
        {
          title.includes('Rate') ? `${(typeof value === 'number' ? value : Number(value)).toFixed(1)}%` :
          title.includes('Cost') ? `$${(typeof value === 'number' ? value : Number(value)).toFixed(2)}` :
          title.includes('Duration') ? `${(typeof value === 'number' ? value : Number(value)).toFixed(1)}s` :
          value
        }
      </Typography>
      {trend !== undefined && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mt: 0.5,
          color: getTrendColor(trend)
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: alpha(getTrendColor(trend), 0.1),
            fontSize: '0.75rem',
            fontWeight: 'bold'
          }}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(typeof trend === 'number' ? trend : Number(trend)).toFixed(1)}%
          </Box>
        </Box>
      )}
    </Card>
  );
};

const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [client, setClient] = useState(null);
  const [clientAgents, setClientAgents] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('current_month');
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    totalCalls: 0,
    successRate: 0,
    avgDuration: 0,
    costPerCall: 0,
    totalLeads: 0,
    totalEnquiries: 0,
    completionRate: 0,
    trends: {}
  });
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Total Calls',
        data: [],
        borderColor: '#0A84FF',
        backgroundColor: 'rgba(10, 132, 255, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#0A84FF'
      },
      {
        label: 'Leads',
        data: [],
        borderColor: '#34C759',
        backgroundColor: 'rgba(52, 199, 89, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#34C759'
      },
      {
        label: 'Enquiries',
        data: [],
        borderColor: '#5856D6',
        backgroundColor: 'rgba(88, 86, 214, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#5856D6'
      }
    ]
  });
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [recentCalls, setRecentCalls] = useState([]);

  // Fetch client details on component mount
  useEffect(() => {
    fetchClientDetails();
  }, [clientId]);

  // Fetch stats when time range or selected agent changes
  useEffect(() => {
    if (client) {
      fetchClientStats();
      fetchChartData();
      fetchRecentCalls();
      fetchClientUsers();
    }
  }, [client, timeRange, selectedAgent]);

  // Fetch client details
  const fetchClientDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organizations/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClient(data.organization);
        
        // Fetch client's agents
        fetchClientAgents();
      } else {
        console.error('Failed to fetch client details:', response.status);
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch client's agents
  const fetchClientAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organizations/${clientId}/agents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClientAgents(data.agents || []);
      } else {
        console.error('Failed to fetch client agents:', response.status);
      }
    } catch (error) {
      console.error('Error fetching client agents:', error);
    }
  };

  // Format date range for API requests
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;

    switch (timeRange) {
      case 'current_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'previous_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'all_time':
        // For "all_time", use a very early date (Jan 1, 2000) instead of going too far back
        startDate = new Date(2000, 0, 1);
        endDate = now;
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = now;
    }

    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString()
    };
  };

  // Fetch client stats
  const fetchClientStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const dateRange = getDateRange();
      const agentParam = selectedAgent !== 'all' ? `&agent_id=${selectedAgent}` : '';
      
      const url = `${process.env.REACT_APP_API_URL}/api/organizations/${clientId}/stats?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}${agentParam}`;
      console.log('Fetching stats from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          totalCalls: data.totalCalls || 0,
          successRate: data.successRate || 0,
          avgDuration: data.avgDuration || 0,
          costPerCall: data.costPerCall || 0,
          totalLeads: data.totalLeads || 0,
          totalEnquiries: data.totalEnquiries || 0,
          completionRate: data.completionRate || 0,
          trends: data.trends || {}
        });
      } else {
        console.error('Failed to fetch client stats:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error fetching client stats:', error);
    }
  };

  // Fetch chart data
  const fetchChartData = async () => {
    try {
      const token = localStorage.getItem('token');
      const dateRange = getDateRange();
      const agentParam = selectedAgent !== 'all' ? `&agent_id=${selectedAgent}` : '';
      
      const url = `${process.env.REACT_APP_API_URL}/api/organizations/${clientId}/chart-data?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}${agentParam}`;
      console.log('Fetching chart data from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setChartData({
          labels: data.labels || [],
          datasets: [
            {
              ...chartData.datasets[0],
              data: data.totalCalls || []
            },
            {
              ...chartData.datasets[1],
              data: data.leads || []
            },
            {
              ...chartData.datasets[2],
              data: data.enquiries || []
            }
          ]
        });
      } else {
        console.error('Failed to fetch chart data:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  // Fetch recent calls
  const fetchRecentCalls = async () => {
    try {
      const token = localStorage.getItem('token');
      const dateRange = getDateRange();
      const agentParam = selectedAgent !== 'all' ? `&agent_id=${selectedAgent}` : '';
      
      const url = `${process.env.REACT_APP_API_URL}/api/organizations/${clientId}/calls?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}${agentParam}&limit=10`;
      console.log('Fetching recent calls from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRecentCalls(data.calls || []);
      } else {
        console.error('Failed to fetch recent calls:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error fetching recent calls:', error);
    }
  };

  // Fetch client users
  const fetchClientUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/organizations/${clientId}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClientUsers(data.users || []);
      } else {
        console.error('Failed to fetch client users:', response.status);
      }
    } catch (error) {
      console.error('Error fetching client users:', error);
    }
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'start',
        labels: {
          usePointStyle: true,
          padding: 25,
          boxWidth: 8,
          boxHeight: 8,
          font: {
            size: 12,
            weight: 500
          },
          color: '#6B7280'
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          padding: 10,
          color: '#6B7280',
          font: {
            size: 11,
            weight: 500
          }
        },
        border: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          borderDash: [2],
          drawBorder: false,
          color: alpha('#E5E7EB', 0.1)
        },
        ticks: {
          padding: 10,
          color: '#6B7280',
          font: {
            size: 11,
            weight: 500
          }
        },
        border: {
          display: false
        }
      }
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 1.5,
        fill: true,
      },
      point: {
        radius: 0,
        hitRadius: 8,
        hoverRadius: 4,
        hoverBorderWidth: 2,
        hoverBackgroundColor: 'white'
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format duration to minutes and seconds
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Client not found or you don't have permission to view it.</Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/clients')}
          sx={{ mt: 2 }}
        >
          Back to Clients
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ pt: 4, pb: 2 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/clients')}
          sx={{ mb: 2 }}
        >
          Back to Clients
        </Button>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h4" component="h1">
              {client.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Created: {formatDate(client.created_at)}
            </Typography>
          </Grid>
          <Grid item>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                displayEmpty
                startAdornment={<DateRangeIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="current_month">Current Month</MenuItem>
                <MenuItem value="previous_month">Previous Month</MenuItem>
                <MenuItem value="all_time">All Time</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <FormControl variant="outlined" size="small" sx={{ mb: 3, minWidth: 200 }}>
          <Select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            displayEmpty
          >
            <MenuItem value="all">All Agents</MenuItem>
            {clientAgents.map((agent) => (
              <MenuItem key={agent.id} value={agent.id}>
                {agent.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Calls" 
              value={stats.totalCalls} 
              trend={stats.trends?.totalCallsTrend}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Success Rate" 
              value={stats.successRate} 
              trend={stats.trends?.successRateTrend}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Avg Duration" 
              value={stats.avgDuration}
              trend={stats.trends?.avgDurationTrend}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Completion Rate" 
              value={stats.completionRate}
              trend={stats.trends?.completionRateTrend}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Leads" 
              value={stats.totalLeads}
              trend={stats.trends?.totalLeadsTrend}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Enquiries" 
              value={stats.totalEnquiries}
              trend={stats.trends?.totalEnquiriesTrend}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Cost Per Call" 
              value={stats.costPerCall}
              trend={stats.trends?.costPerCallTrend}
            />
          </Grid>
        </Grid>
        
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Call Analytics" />
          <Tab label="Agents Performance" />
          <Tab label="Recent Calls" />
          <Tab label="Users" />
        </Tabs>
        
        {tabValue === 0 && (
          <Card sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Call Volume
            </Typography>
            <Box sx={{ height: 380 }}>
              <Line data={chartData} options={chartOptions} />
            </Box>
          </Card>
        )}
        
        {tabValue === 1 && (
          <Card sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Agent Performance
            </Typography>
            {clientAgents.length === 0 ? (
              <Alert severity="info">No agents assigned to this client yet.</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Agent Name</TableCell>
                      <TableCell align="right">Total Calls</TableCell>
                      <TableCell align="right">Success Rate</TableCell>
                      <TableCell align="right">Avg Duration</TableCell>
                      <TableCell align="right">Leads Generated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientAgents.map((agent) => (
                      <TableRow key={agent.id}>
                        <TableCell>{agent.name}</TableCell>
                        <TableCell align="right">{agent.stats?.totalCalls || 0}</TableCell>
                        <TableCell align="right">{agent.stats?.successRate ? `${agent.stats.successRate.toFixed(1)}%` : '0%'}</TableCell>
                        <TableCell align="right">{agent.stats?.avgDuration ? formatDuration(agent.stats.avgDuration) : '0:00'}</TableCell>
                        <TableCell align="right">{agent.stats?.totalLeads || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        )}
        
        {tabValue === 2 && (
          <Card sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Recent Calls
            </Typography>
            {recentCalls.length === 0 ? (
              <Alert severity="info">No calls found for the selected period.</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell align="right">Cost</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentCalls.map((call) => (
                      <TableRow key={call.id}>
                        <TableCell>{formatDate(call.datetime)}</TableCell>
                        <TableCell>{call.agent_name || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={call.is_lead ? 'Lead' : call.is_enquiry ? 'Enquiry' : 'Call'} 
                            size="small"
                            color={call.is_lead ? 'success' : call.is_enquiry ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{formatDuration(call.duration)}</TableCell>
                        <TableCell align="right">${call.cost ? Number(call.cost).toFixed(2) : '0.00'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={call.completed ? 'Completed' : 'Incomplete'} 
                            size="small"
                            color={call.completed ? 'success' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        )}
        
        {tabValue === 3 && (
          <Card sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Client Users
            </Typography>
            {clientUsers.length === 0 ? (
              <Alert severity="info">No users found for this client.</Alert>
            ) : (
              <List>
                {clientUsers.map((user) => (
                  <ListItem key={user.id} divider>
                    <ListItemIcon>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={user.email} 
                      secondary={`Role: ${user.role}${user.name ? ` • Name: ${user.name}` : ''}`}
                    />
                    <Chip 
                      icon={<EmailIcon />} 
                      label={user.email}
                      variant="outlined"
                      color="primary"
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default ClientDetails; 